import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import Ukraine from '../langs/uk.json';
import Poland from '../langs/pl.json';
import English from '../langs/en.json';
import Spanish from '../langs/es.json';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from '../redux/user/selector';
import { useTranslation } from 'react-i18next';
import { updateUserAppLang } from 'src/redux/user/thunk';

const EnglishLang = { name: 'English', code: 'en', isWorkInProgress: false };
const UkrainianLang = { name: 'Українська', code: 'uk', isWorkInProgress: false };
const PolishLang = { name: 'Polski', code: 'pl', isWorkInProgress: false };
const SpanishLang = { name: 'Español', code: 'es', isWorkInProgress: false };

const GermanLang = { name: 'Deutsch', code: 'de', isWorkInProgress: true };
const FrenchLang = { name: 'Français', code: 'fr', isWorkInProgress: true };

const DEFAULT_LOCALE = EnglishLang.code;
const availableLanguages = [EnglishLang, SpanishLang, FrenchLang, GermanLang, UkrainianLang, PolishLang];

type LangsProviderType = {
  children: ReactNode;
};

type LangsContextType = {
  locale: string;
  changeLocale: (lang: string) => void;
  availableLanguages: { name: string; code: string; isWorkInProgress: boolean }[];
  defaultLocale: typeof DEFAULT_LOCALE;
};

const LangsContext = createContext<LangsContextType>({
  locale: DEFAULT_LOCALE,
  availableLanguages: [],
  defaultLocale: DEFAULT_LOCALE,
  changeLocale: () => {},
});

export const LangsProvider: FC<LangsProviderType> = ({ children }): JSX.Element => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();

  const [locale, setLocale] = useState(
    availableLanguages.find((l) => l.code === i18n.language && !l.isWorkInProgress)?.code ?? DEFAULT_LOCALE
  );
  const [messages, setMessages] = useState(Ukraine);

  const currentClient = useSelector(userSelector);

  const changeLocale = useCallback(
    async (lang: string) => {
      if (!availableLanguages.find((l) => l.code === lang && !l.isWorkInProgress)) return;

      i18n.changeLanguage(lang);
      if (currentClient?.id) {
        await dispatch(updateUserAppLang(lang)).unwrap();
      }
      localStorage.setItem('mindlyLocale', lang);
    },
    [currentClient?.id, availableLanguages?.length]
  );

  useEffect(() => {
    if (!currentClient?.id) return;

    changeLocale(currentClient.app_lang);
  }, [currentClient?.id, changeLocale]);

  // Sync locale state when i18n language changes
  useEffect(() => {
    if (availableLanguages.find((l) => l.code === i18n.language && !l.isWorkInProgress)) {
      setLocale(i18n.language);
    }
  }, [i18n.language]);

  useEffect(() => {
    switch (locale) {
      case 'uk':
        setMessages(Ukraine);
        break;
      case 'pl':
        setMessages(Poland);
        break;
      case 'en':
        setMessages(English);
        break;
      case 'es':
        setMessages(Spanish);
        break;
    }

    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [locale]);

  return (
    <LangsContext.Provider
      value={{
        locale,
        changeLocale,
        availableLanguages,
        defaultLocale: DEFAULT_LOCALE,
      }}
    >
      <IntlProvider locale={locale} messages={messages}>
        {children}
      </IntlProvider>
    </LangsContext.Provider>
  );
};

/**
 * @deprecated use const {i18n: { language }} = useTranslation();
 */
export default function useLangs() {
  return useContext(LangsContext);
}
